import { useMockData } from "../constants/environment";
import { Event, EventCategory, EventStatus} from "../models/event/event";
import { EnrollmentMode } from "../models/enrollment/enrollment";
import { getEventsLocalized } from "../mock-data/event/events-mock-data";
import { CacheUtil } from "../utils/cache-util";
import { Language } from "../constants/language";
import {PaginatedResult, PAGINATION, PaginationUtil} from "../utils/pagination-util";
import { DateUtil } from "../utils/date-util";

export interface EventFilter {
  category?: EventCategory[];
  status?: EventStatus[];
  featured?: boolean;
  enrollmentMode?: EnrollmentMode[];
  startDate?: string;
  endDate?: string;
  tags?: string[];
  search?: string;
  [key: string]: any;
}

export interface EventSortOption {
  field: keyof Event | string;
  direction: 'asc' | 'desc';
}

class EventService {
  
  private static readonly EVENT_DATA_CACHE_KEY = "eventContent";

  /*************
   * Get Events
   **************/
  public async getEvents(
    language: Language = Language.AR,
    filter?: EventFilter,
    sort?: EventSortOption,
    page: number = PAGINATION.DEFAULT_PAGE,
    limit: number = PAGINATION.DEFAULT_PAGE_SIZE,
    fetchAll: boolean = false
  ): Promise<PaginatedResult<Event>> {

    let events: Event[] = [];


    const cachedData = CacheUtil.get<Event[]>(EventService.EVENT_DATA_CACHE_KEY);
    if (cachedData) {
      events = cachedData;
    }
    else {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 300));

        const result = getEventsLocalized(language, true);
        events = result.data;
        CacheUtil.set(EventService.EVENT_DATA_CACHE_KEY, events);
      } else {
        //TODO USE API CALL TO FETCH REAL DATA
        const result = getEventsLocalized(language, true);
        events = result.data;
        CacheUtil.set(EventService.EVENT_DATA_CACHE_KEY, events);

      }
    }

    if (filter) events = this.applyFilters(events, filter);
    if (sort)   events = this.applySorting(events, sort);


    const paginatedResult = PaginationUtil.paginate<Event>(events, page, limit);
    return paginatedResult;
  }

  public async getEventById(id: number, language: Language = Language.AR): Promise<Event | undefined> {
    const eventResults = await this.getEvents(language, undefined, undefined, PAGINATION.DEFAULT_PAGE, -1);
    const events: Event[] = eventResults.data ? eventResults.data : [];
    return events.find(event => event.id === id);
  }

  public async getEventByKey(key: string, language: Language = Language.AR): Promise<Event | undefined> {
    const eventResults = await this.getEvents(language, undefined, undefined, PAGINATION.DEFAULT_PAGE, -1);
    const events: Event[] = eventResults.data ? eventResults.data : [];
    return events.find(event => event.key === key);
  }

  public async getUpcomingEvents(language: Language = Language.AR, limit: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<Event[]> {
    const eventResults = await this.getEvents(language, undefined, undefined, PAGINATION.DEFAULT_PAGE, -1);
    const events: Event[] = eventResults.data ? eventResults.data : [];
    const now = new Date();
    // Set time to start of day for fair comparison
    now.setHours(0, 0, 0, 0);

    const upcomingEvents = events.filter(event => {
      const eventDate = DateUtil.parseDateString(event.startDate);
      return eventDate >= now;
    }).sort((a, b) => DateUtil.parseDateString(a.startDate).getTime() - DateUtil.parseDateString(b.startDate).getTime());

    return limit ? upcomingEvents.slice(0, limit) : upcomingEvents;
  }

  /*************
   * Search & Filter
  **************/
  public async searchEvents(query: string, language: Language = Language.AR): Promise<Event[]> {
    const eventResults = await this.getEvents(language, undefined, undefined, PAGINATION.DEFAULT_PAGE, -1);
    const events: Event[] = eventResults.data ? eventResults.data : [];
    const searchTerm = query.toLowerCase();

    return events.filter(event =>
      event.title.toLowerCase().includes(searchTerm) ||
      event.description.toLowerCase().includes(searchTerm) ||
      event.shortDescription.toLowerCase().includes(searchTerm) ||
      event.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
      (event.organizer?.title || '').toLowerCase().includes(searchTerm) ||
      event.address.name.toLowerCase().includes(searchTerm)
    );
  }

  private applyFilters(events: Event[], filter: EventFilter): Event[] {
    return events.filter(event => {
      if (filter.category && filter.category.length > 0) {
        if (!filter.category.includes(event.category)) return false;
      }

      if (filter.status && filter.status.length > 0) {
        if (!filter.status.includes(event.status)) return false;
      }

      if (filter.featured !== undefined) {
        if (event.featured !== filter.featured) return false;
      }

      if (filter.enrollmentMode && filter.enrollmentMode.length > 0) {
        if (!filter.enrollmentMode.includes(event.enrollment.type)) return false;
      }

      if (filter.startDate || filter.endDate) {
        const startDate = filter.startDate ? DateUtil.parseDateString(filter.startDate) : new Date('1900-01-01');
        const endDate = filter.endDate ? DateUtil.parseDateString(filter.endDate) : new Date('2100-12-31');
        const eventStart = DateUtil.parseDateString(event.startDate);
        const eventEnd = DateUtil.parseDateString(event.endDate);
        if (!(eventStart <= endDate && eventEnd >= startDate)) return false;
      }

      if (filter.tags && filter.tags.length > 0) {
        if (!filter.tags.some(tag => event.tags.includes(tag))) return false;
      }

      if (filter.search && filter.search.trim()) {
        const searchTerm = filter.search.toLowerCase();
        const matches = 
          event.title.toLowerCase().includes(searchTerm) ||
          event.description.toLowerCase().includes(searchTerm) ||
          event.shortDescription.toLowerCase().includes(searchTerm) ||
          event.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
          (event.organizer?.title || '').toLowerCase().includes(searchTerm) ||
          event.address.name.toLowerCase().includes(searchTerm);
        if (!matches) return false;
      }

      return true;
    });
  }

  private applySorting(events: Event[], sort: EventSortOption): Event[] {
    return [...events].sort((a, b) => {
      let aValue: any = a[sort.field as keyof Event];
      let bValue: any = b[sort.field as keyof Event];

      // Handle date fields using DateUtil for proper DD-MM-YYYY parsing
      if (sort.field === 'startDate' || sort.field === 'endDate') {
        aValue = DateUtil.parseDateString(aValue).getTime();
        bValue = DateUtil.parseDateString(bValue).getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sort.direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }

  public isEventUpcoming(event: Event): boolean {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const eventStart = DateUtil.parseDateString(event.startDate);
    return eventStart >= now;
  }

  public isEventOngoing(event: Event): boolean {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const eventStart = DateUtil.parseDateString(event.startDate);
    const eventEnd = DateUtil.parseDateString(event.endDate);
    return now >= eventStart && now <= eventEnd;
  }

  public isEventPast(event: Event): boolean {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const eventEnd = DateUtil.parseDateString(event.endDate);
    return eventEnd < now;
  }
  /*************
   * Enrollment
  **************/
  public canEnroll(event: Event): boolean {
    if (!this.isEventUpcoming(event)) return false;
    if (event.enrollment.type === EnrollmentMode.NONE) return false;
    if (event.enrollment.deadline) {
      const deadline = DateUtil.parseDateString(event.enrollment.deadline);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (now > deadline) return false;
    }
    if (event.enrollment.capacity && event.enrollment.enrolled) {
      if (event.enrollment.enrolled >= event.enrollment.capacity) return false;
    }
    return true;
  }

  public getEnrollmentSpotsRemaining(event: Event): number | null {
    if (!event.enrollment.capacity || !event.enrollment.enrolled) return null;
    return Math.max(0, event.enrollment.capacity - event.enrollment.enrolled);
  }

  public getEnrollmentProgress(event: Event): number | null {
    if (!event.enrollment.capacity || !event.enrollment.enrolled) return null;
    return Math.min(100, (event.enrollment.enrolled / event.enrollment.capacity) * 100);
  }
}

// Export the service instance using default export pattern to avoid initialization issues
const eventServiceInstance = new EventService();
export { eventServiceInstance as eventService };
export default eventServiceInstance;